/**
 * ChargeDetailsModal - Modal premium de detalhes da cobrança
 * 
 * Layout alinhado ao padrão do CreateChargeModal: seções com ícone + título h3,
 * separadas por Separator. Sem cards (SectionCard).
 * 
 * Todos os dados são carregados via RPC centralizada (get_cobranca_details).
 * 
 * @author Módulo Financeiro
 * @version 7.2.0
 * RELEASE: 2026-03-15 v1.0.0 - Criação do modal de detalhes
 * RELEASE: 2026-03-15 v6.0.0 - Migração para RPC centralizada (get_cobranca_details)
 * RELEASE: 2026-03-16 v7.0.0 - Redesign layout padrão CreateChargeModal (Separator sections)
 * RELEASE: 2026-06-09 v7.2.0 - Permissão `financial_charges` resolvida internamente via usePermissions;
 *                              props canEdit/canDelete passam a ser override opcional. Garante mesmo
 *                              comportamento de rodapé em todos os openers (Cobranças, Subscription,
 *                              Notificações, Deal, PaymentLinks, Dashboard) sem duplicar regras.
 * RELEASE: 2026-10-19 v7.4.0 - Nome/avatar do cliente clicáveis: abrem CustomerDetailsModalPremium
 *                              por cima da cobrança (somente quando há cliente vinculado).
 * RELEASE: 2026-10-19 v7.5.0 - Seta (ArrowUpRight) no título da seção Cliente, padronizando com a
 *                              seção Pagador do SubscriptionDetailsModal: sinaliza que a ficha do
 *                              cliente abre a partir da cobrança. Desabilitada sem cliente vinculado.

 */

import { FinanceiroVehiclesBlock } from '@/components/financial/FinanceiroVehiclesBlock';
import { useState, useMemo, useRef, useEffect, lazy, Suspense } from 'react';
// v2.0.0: EditChargeProviderModal removido — edição unificada via CreateChargeModal
import { parseDateLocal } from '@/lib/utils';
import { formatPhoneForDisplay } from '@/lib/phone-utils';
import { formatDocumentForDisplay } from '@/lib/input-masks';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
// v7.5.0 — tooltip do atalho de seta na seção Cliente (mesmo padrão do modal de recorrência)
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import {
  Copy,
  ExternalLink,
  MessageCircle,
  RefreshCw,
  Pencil,
  Loader2,
  AlertTriangle,
  Receipt,
  Calendar,
  CreditCard,
  DollarSign,
  FileText,
  List,
  History,
  CheckCircle2,
  ShoppingCart,
  Landmark,
  Package,
  User,
  Shield,
  Briefcase,
  ClipboardList,
  ChevronRight,
  X,
  XCircle,
  FileCheck,
  Paperclip,
  Rocket,
  Repeat,
  Calculator,
  Info,
  Lock,
  ArrowUpRight,
} from 'lucide-react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAssinaturaActions } from '@/hooks/useAssinaturaActions';
import { formatDealCode } from '@/lib/formatters';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { ChargeProviderActions } from './ChargeProviderActions';
import { ChargeTimeline } from './ChargeTimeline';
import { ChargeRemindersSection } from './ChargeRemindersSection';
import { SmartAvatar } from '@/components/ui/smart-avatar';
// v7.4.0: ficha oficial do cliente (mesma usada em Veículos/CRM)
import { CustomerDetailsModalPremium } from '@/components/crm/customers/modal/CustomerDetailsModalPremium';
import { CreateChargeModal } from './CreateChargeModal';
import { MarkAsPaidModal } from './MarkAsPaidModal';
import { CancelChargeDialog } from './CancelChargeDialog';
// v9.4.0: estorno de baixa manual
import { UndoManualPaymentDialog } from './UndoManualPaymentDialog';

import { EditChargeProviderModal } from './EditChargeProviderModal';
// v9.6.0: fluxo de parcela individual em conta sem integração
import { EditParcelaManualModal } from './EditParcelaManualModal';
import { useUpdateParcelaManual } from '@/hooks/useUpdateParcelaManual';

import { useCobrancaDetails } from '@/hooks/useCobrancaDetails';
import { useReceiptSignedUrl } from '@/hooks/useReceiptSignedUrl';
import { usePermissions } from '@/hooks/usePermissions';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { SubscriptionJurosSection } from '@/components/financial/subscription/SubscriptionJurosSection';
import { SubscriptionExpiracaoSection } from '@/components/financial/subscription/SubscriptionExpiracaoSection';
import { SubscriptionDetailsModal } from '@/components/financial/SubscriptionDetailsModal';
import { CobrancaMemoriaCalculoModal } from '@/components/financial/CobrancaMemoriaCalculoModal';
import { AnticipateConfirmDialog } from '@/components/financial/AnticipateConfirmDialog';
import { useSyncCharge } from '@/hooks/financial/useSyncCharge';
import { useRetryCancellationSync } from '@/hooks/useRetryCancellationSync';
import { useChargeCardSummary } from '@/hooks/financial/useSavedCards';
import { resolveCheckoutUrl } from '@/lib/financial/resolveCheckoutUrl';
import { Building2, ShieldCheck, Undo2 } from 'lucide-react';

import type { FinanceiroCobranca, FinanceiroStatus, TipoCobranca } from '@/hooks/useFinanceiroCobrancas';
import { createLogger } from '@/lib/logger';

/** Lazy load do modal de detalhes do negócio para evitar bundle desnecessário */
const DealDetailsModalPremium = lazy(() =>
  import('@/components/crm/deals/modal/DealDetailsModalPremium').then(m => ({ default: m.DealDetailsModalPremium }))
);

/** Lazy load do modal de detalhes da tarefa (cobranças originadas de Retenção/Jurídico/Normal) */
const TaskDetailsModal = lazy(() =>
  import('@/components/tasks/modal/TaskDetailsModal').then(m => ({ default: m.TaskDetailsModal }))
);
import { useQueryClient } from '@tanstack/react-query';
import { PaymentMethodIcon, DefaultPaymentIcon } from '@/components/icons/PaymentMethodIcons';

const logger = createLogger('ChargeDetailsModal');

/** Mapa de cores dos status */
const statusConfig: Record<FinanceiroStatus, { label: string; className: string; dotClass: string }> = {
  aguardando_emissao: { label: 'Aguardando emissão', className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300', dotClass: 'bg-slate-400' },
  pendente: { label: 'Pendente', className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', dotClass: 'bg-yellow-500' },
  edicao_andamento: { label: 'Edição em andamento', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', dotClass: 'bg-amber-500' },
  aguardando_pagamento: { label: 'Aguardando', className: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400', dotClass: 'bg-orange-500' },
  pago: { label: 'Pago', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', dotClass: 'bg-green-500' },
  parcial: { label: 'Parcial', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', dotClass: 'bg-blue-500' },
  vencido: { label: 'Vencido', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', dotClass: 'bg-red-500' },
  cancelado: { label: 'Cancelado', className: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400', dotClass: 'bg-gray-500' },
  processando_cancelamento: { label: 'Processando cancelamento', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400', dotClass: 'bg-amber-500 animate-pulse' },
  falha_cancelamento: { label: 'Falha ao cancelar no provedor', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', dotClass: 'bg-red-500' },
};

const tipoLabels: Record<TipoCobranca, string> = {
  unica: 'Única',
  parcelada: 'Parcelada',
  recorrente: 'Recorrente',
};

/** Mapa de labels para tipos de pagamento */
const tipoPagamentoLabels: Record<string, string> = {
  pix: 'PIX',
  boleto: 'Boleto',
  carne: 'Carnê',
  cartao_credito: 'Cartão de Crédito',
  cartao_debito: 'Cartão de Débito',
  dinheiro: 'Dinheiro',
  doc: 'DOC',
  ted: 'TED',
  cheque: 'Cheque',
  nota_promissoria: 'Nota Promissória',
  outros: 'Outros',
};

interface ChargeDetailsModalProps {
  /** Cobrança selecionada (objeto básico da listagem) */
  cobranca: FinanceiroCobranca | null;
  /** Controla abertura do modal */
  open: boolean;
  /** Callback para fechar */
  onOpenChange: (open: boolean) => void;
  /** v7.1.0: Permissão para editar (oculta botões "Editar" quando false) */
  canEdit?: boolean;
  /** v7.1.0: Permissão para excluir/cancelar (reservado para uso futuro) */
  canDelete?: boolean;
}

/**
 * Subcomponente interno: resolve signed URL do comprovante e renderiza
 * ImageLightbox interno (preview consistente — imagens e PDFs).
 * RELEASE: 2026-06-09 v1.0.0 — Criação para preview interno do comprovante manual.
 */
function ReceiptLightboxHost({
  receipt,
  onClose,
}: {
  receipt: { path: string; name: string } | null;
  onClose: () => void;
}) {
  const { url } = useReceiptSignedUrl(receipt?.path ?? null);
  const open = !!receipt && !!url;
  const mediaItems: MediaItem[] = useMemo(() => {
    if (!receipt || !url) return [];
    // Deriva o tipo (image/video/document) pela extensão do nome do arquivo
    const ext = receipt.path.split('.').pop()?.toLowerCase() || '';
    const mediaType: MediaItem['type'] =
      ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext) ? 'image'
      : ['mp4', 'webm', 'mov'].includes(ext) ? 'video'
      : 'document';
    return [{ url, type: mediaType, title: receipt.name }];
  }, [receipt, url]);
  if (!receipt) return null;
  return (
    <ImageLightbox
      open={open}
      onClose={onClose}
      mediaItems={mediaItems}
      initialIndex={0}
    />
  );
}

/**
 * Modal premium de detalhes da cobrança com ações integradas
 * Componente reutilizável em páginas de cobranças, negócios e clientes
 * Todos os dados carregados via RPC centralizada (get_cobranca_details)
 */
/** Botão de retry para cobrança em falha_cancelamento (v9.3.0). */
function RetryCancellationButton({ cobrancaId, parcelaId }: { cobrancaId?: string | null; parcelaId?: string | null }) {
  const retry = useRetryCancellationSync();
  return (
    <Button
      variant="outline"
      size="xs"
      className="gap-1.5"
      onClick={() => retry.mutate({ cobranca_id: cobrancaId ?? null, parcela_id: parcelaId ?? null })}
      disabled={retry.isPending}
    >
      {retry.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      Tentar novamente
    </Button>
  );
}

/**
 * Fase 5 — seção "Cartão usado" exibida no modal de detalhes da cobrança
 * Mostra bandeira, últimos 4 dígitos, validade, holder mascarado e badge 3DS.
 */
function ChargeCardUsedSection({ cobrancaId }: { cobrancaId: string }) {
  const { data: card, isLoading } = useChargeCardSummary(cobrancaId);
  if (isLoading || !card) return null;
  return (
    <>
      <section>
        <div className="flex items-center gap-2 mb-3">
          <CreditCard className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Cartão usado</h3>
        </div>
        <div className="flex items-center gap-3 p-3 rounded-md border bg-muted/30">
          <div className="h-9 w-12 rounded bg-background flex items-center justify-center">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium">
                {card.brand?.toUpperCase() || 'CARTÃO'} •••• {card.last4}
              </span>
              {card.three_ds_used && (
                <Badge variant="outline" className="text-[10px] gap-1 text-green-600 border-green-200">
                  <ShieldCheck className="h-3 w-3" />
                  3D Secure
                </Badge>
              )}
              <Badge variant="secondary" className="text-[10px]">{card.attempt_status}</Badge>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {String(card.exp_month).padStart(2, '0')}/{card.exp_year}
              {card.holder_name_masked && <> · {card.holder_name_masked}</>}
              {card.provider_charge_id && <> · ID provedor: {card.provider_charge_id.substring(0, 12)}…</>}
            </div>
          </div>
        </div>
      </section>
      <Separator />
    </>
  );
}

export function ChargeDetailsModal({ cobranca, open, onOpenChange, canEdit, canDelete }: ChargeDetailsModalProps) {
  // v7.2.0: Permissão resolvida internamente para unificar comportamento entre todos os openers.
  // Props canEdit/canDelete continuam aceitas como override explícito quando necessário.
  const { canEdit: canEditPerm, canDelete: canDeletePerm } = usePermissions();
  const effectiveCanEdit = canEdit ?? canEditPerm('financial_charges');
  const effectiveCanDelete = canDelete ?? canDeletePerm('financial_charges');
  const [editOpen, setEditOpen] = useState(false);
  // v2.0.0: editProviderOpen removido — fluxo unificado em editOpen (CreateChargeModal)

  const [resending, setResending] = useState(false);
  /** Controla abertura do modal de detalhes do negócio vinculado */
  const [dealModalOpen, setDealModalOpen] = useState(false);
  /** Controla abertura do modal de detalhes da tarefa vinculada (Retenção/Jurídico/Normal) */
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  // v8.0.0: estados de baixa manual e cancelamento
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [markPaidParcela, setMarkPaidParcela] = useState<{ id: string; numero_parcela: number; valor: number; data_vencimento: string; provider_charge_id: string | null; conta_corrente_id: string | null } | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  // v9.4.0: Estado do estorno de baixa manual ("Cancelar recebimento em dinheiro")
  const [undoPaidOpen, setUndoPaidOpen] = useState(false);
  const [undoPaidParcela, setUndoPaidParcela] = useState<{ id: string; numero_parcela: number } | null>(null);
  // v9.1.0: Estados para editar/cancelar UMA parcela individual a partir da seção Parcelas
  const [editParcela, setEditParcela] = useState<any | null>(null);
  const [editParcelaOpen, setEditParcelaOpen] = useState(false);
  // v9.6.0: edição/cancelamento de parcela em cobrança SEM integração (conta manual)
  const [editParcelaManual, setEditParcelaManual] = useState<any | null>(null);
  const [editParcelaManualOpen, setEditParcelaManualOpen] = useState(false);
  const [cancelParcelaManual, setCancelParcelaManual] = useState<any | null>(null);
  const { updateParcela: updateParcelaManual, isSaving: isSavingParcelaManual } = useUpdateParcelaManual();
  // Atalho "Antecipar emissão" — reaproveita a mesma mutation usada na listagem de ciclos
  const [anteciparOpen, setAnteciparOpen] = useState(false);
  // Estado do atalho "Ver recorrência" — abre o modal de detalhes da assinatura vinculada
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  // v7.3.0: Modal "Memória de cálculo" — abre para cobranças vinculadas a assinatura
  const [memoriaOpen, setMemoriaOpen] = useState(false);
  // v7.4.0: controle da ficha do cliente aberta a partir da cobrança
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  // v9.0.0: Lightbox interno do comprovante de baixa manual
  const [receiptLightbox, setReceiptLightbox] = useState<{ path: string; name: string } | null>(null);
  // Fase 5: popover para gerar link público de pagamento (gatilho de teste)
  // v9.x: estado do popover de geração de link removido — slug transparente agora é gerado
  // proativamente pelo trigger `trg_ensure_checkout_session` toda vez que o provider
  // devolve um link nativo (invoice_url / payment_link). Botão "Gerar link" descontinuado.
  const queryClient = useQueryClient();


  // ── RPC centralizada: todos os dados em uma única chamada ──
  const { data: details, isLoading: loadingDetails } = useCobrancaDetails(cobranca?.id, open);

  // Dados derivados da RPC
  const rpcCobranca = details?.cobranca;
  const customerData = details?.customer;
  const creatorProfile = details?.creator;
  const meioPagamento = details?.meio_pagamento;
  const contaCorrente = details?.conta_corrente;
  const itensCobranca = details?.itens || [];
  const parcelas = details?.parcelas || [];
  const timelineEvents = details?.timeline || [];
  // Feature 1 — dados do cartão padrão da recorrência (RPC v1.5.8)
  const assinaturaBlock = (details as any)?.assinatura ?? null;
  const defaultCardTokenId: string | null = assinaturaBlock?.default_card_token_id ?? null;
  const defaultCardSnapshot = (assinaturaBlock?.default_card_snapshot ?? null) as
    | { brand?: string | null; last4?: string | null; holder_masked?: string | null; type?: string | null }
    | null;
  const hasDefaultCard = !!defaultCardTokenId && !!defaultCardSnapshot;

  // Mapas UUID → nome para resolver meio_pagamento_id / conta_corrente_id no diff da timeline
  const timelineLookups = useMemo(() => {
    const meio_pagamento: Record<string, string> = {};
    const conta_corrente: Record<string, string> = {};
    if (meioPagamento?.id && meioPagamento?.nome) meio_pagamento[meioPagamento.id] = meioPagamento.nome;
    if (contaCorrente?.id && contaCorrente?.nome) conta_corrente[contaCorrente.id] = contaCorrente.nome;
    return { meio_pagamento, conta_corrente };
  }, [meioPagamento, contaCorrente]);


  // ── Auto-scroll da timeline para o último evento (comportamento de chat) ──
  const timelineScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (timelineScrollRef.current && timelineEvents.length > 0) {
      requestAnimationFrame(() => {
        if (timelineScrollRef.current) {
          timelineScrollRef.current.scrollTop = timelineScrollRef.current.scrollHeight;
        }
      });
    }
  }, [timelineEvents]);

  // numero_fatura agora vem diretamente da coluna dedicada na parcela (não precisa de lookup na timeline)

  // ── Objeto resolvido: prioriza dados completos da RPC, fallback para prop mínimo ──
  // Permite abrir o modal via notificação com apenas { id } sem crash
  const r = rpcCobranca || cobranca;

  // Derivações de status e ações — usa RPC como fallback para suportar abertura via notificação (prop mínimo)
  const resolvedStatus = r?.status as FinanceiroStatus | undefined;
  const status = resolvedStatus ? statusConfig[resolvedStatus] : statusConfig.pendente;
  const isPendente = resolvedStatus === 'pendente';
  /** Status que permitem edição com sincronização no provider */
  const isEditableProviderStatus = resolvedStatus === 'aguardando_pagamento' || resolvedStatus === 'vencido' || resolvedStatus === 'edicao_andamento';
  const hasSynced = !!(r?.provider_charge_id);
  /**
   * v9.5.0 — Cobrança de conta SEM integração (controle manual).
   * Regra: conta corrente sem provider_slug e cobrança sem provider_charge_id.
   * Nesses casos não existe provedor para sincronizar/reenviar e não há
   * prazo de expiração de boleto/link — todo o controle é interno.
   */
  const isContaManual = !contaCorrente?.provider_slug && !r?.provider_charge_id;
  // v8.0.0: status que permitem baixa manual / cancelamento manual
  const canMarkPaidStatus = ['pendente', 'edicao_andamento', 'aguardando_pagamento', 'vencido', 'parcial'].includes(resolvedStatus || '');
  const canCancelStatus = ['pendente', 'edicao_andamento', 'aguardando_pagamento', 'vencido', 'parcial', 'aguardando_emissao'].includes(resolvedStatus || '');
  // Atalho "Antecipar emissão" — disponível apenas quando o ciclo está aguardando emissão
  // Feature 2 — permissão dedicada `financial_subscription_anticipate` (debita cartão imediatamente).
  // Mantém também o gate de edição da cobrança para evitar antecipação em estados inválidos.
  const { canCreate: canCreatePerm } = usePermissions();
  const canAnticipatePerm = canCreatePerm('financial_subscription_anticipate');
  const canAntecipar = resolvedStatus === 'aguardando_emissao' && (canEdit ?? canEditPerm('financial_charges')) && canAnticipatePerm;
  const { anteciparCiclo } = useAssinaturaActions(((r as any)?.assinatura_id as string | null | undefined) ?? null);
  // Para cobranças avulsas: primeira parcela em aberto é a candidata padrão para baixa rápida
  const firstOpenParcela = parcelas.find(p => ['pendente', 'edicao_andamento', 'aguardando_pagamento', 'vencido', 'parcial'].includes(p.status));
  // v9.4.0: primeira parcela paga com baixa MANUAL — candidata ao estorno
  // ("Cancelar recebimento em dinheiro"). Baixas confirmadas por webhook do
  // provedor não podem ser desfeitas pelo usuário (a RPC também bloqueia).
  const firstManualPaidParcela = parcelas.find(
    p => p.status === 'pago'
      && ((p as { paid_source?: string | null }).paid_source || null) === 'manual'
  );
  // v9.2.0: bloqueio de edição da cobrança-pai quando há parcela finalizada
  // (pago/cancelado/estornado). Edição deve ser feita parcela a parcela para
  // evitar reaplicar valor/vencimento em parcelas já liquidadas pelo provider.
  const isParcelada = (r as any)?.tipo_cobranca === 'parcelada';
  const hasFinalizedParcela = parcelas.some(p => ['pago', 'cancelado', 'estornado'].includes(p.status));
  const canEditParent = !(isParcelada && hasFinalizedParcela);
  // ─── Resolução do link de pagamento (checkout transparente vs link do provider) ───
  // v1.1.2: aplica `resolveCheckoutUrl` honrando o `checkout_mode` da conta corrente
  // e o `checkout_session_slug` cacheado na cobrança. Mantém fallback automático
  // (transparent → provider, ou provider → transparent) caso o preferido não esteja
  // disponível. Garante que TODOS os botões/links do modal (header, WhatsApp, copiar,
  // tabela de parcelas, ícones mobile) respeitem a configuração da conta corrente.
  const checkoutMode = contaCorrente?.checkout_mode ?? 'transparent';
  const headerSlug = rpcCobranca?.checkout_session_slug ?? null;
  const resolvedHeader = resolveCheckoutUrl({
    checkout_mode: checkoutMode,
    checkout_session_slug: headerSlug,
    provider_invoice_url: rpcCobranca?.provider_invoice_url ?? cobranca?.provider_invoice_url,
    provider_payment_link: rpcCobranca?.provider_payment_link ?? cobranca?.provider_payment_link,
  });
  const paymentLink = resolvedHeader.url;
  const headerUsedFallback = resolvedHeader.usedFallback;


  if (!cobranca) return null;

  /** Helper seguro para formatar datas — retorna '—' se a string não existir */
  const safeFormatDate = (dateStr: string | null | undefined, fmt = 'dd/MM/yyyy') => {
    if (!dateStr) return '—';
    try {
      return format(parseDateLocal(dateStr.substring(0, 10)), fmt, { locale: ptBR });
    } catch {
      return '—';
    }
  };

  // Nome do cliente: fallback entre PaginatedCobranca e FinanceiroCobranca
  const customerName = customerData?.name || (cobranca as any).customer_name || cobranca.customer?.name || null;
  // v7.4.0: ID do cliente para abrir a ficha (RPC de detalhes primeiro; fallback no objeto da listagem)
  const chargeCustomerId: string | null =
    (rpcCobranca as any)?.customer_id || (cobranca as any)?.customer_id || null;

  /** Formata valor monetário */
  const formatCurrency = (value: number, moeda = 'BRL') =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: moeda }).format(value);

  /** Copia texto para clipboard */
  const handleCopyLink = async () => {
    if (!paymentLink) return;
    try {
      await navigator.clipboard.writeText(paymentLink);
      toast.success('Link de pagamento copiado!');
    } catch {
      toast.error('Erro ao copiar link');
    }
  };

  /** Envia link de pagamento via WhatsApp */
  const handleSendWhatsApp = () => {
    if (!paymentLink || r?.valor_total == null) return;
    const valor = formatCurrency(r.valor_total, (r as any).moeda);
    const mensagem = encodeURIComponent(
      `Olá! Segue o link para pagamento da cobrança no valor de ${valor}:\n\n${paymentLink}\n\nQualquer dúvida, estamos à disposição.`
    );
    window.open(`https://wa.me/?text=${mensagem}`, '_blank');
  };

  /**
   * Reenvia cobrança ao provider.
   *
   * Arquitetura server-side robusta: enviamos APENAS o `cobranca_id`.
   * A edge function `financial-charge` (action `resend_to_provider`)
   * resolve TODOS os campos sensíveis (valor, cliente, conta corrente,
   * vencimento, forma de pagamento) via RPC `resolve_cobranca_for_resend`
   * no banco, com:
   *   - validação de tenant ownership
   *   - validação de status / sincronia prévia
   *   - lease atômico de 60s para impedir dupla submissão
   *   - auditoria em security_bypass_attempts
   *
   * Funciona uniformemente para qualquer ponto de entrada do modal
   * (Listagem de Cobranças, Recorrência, Dashboard, Notificação), inclusive
   * quando o componente pai passa apenas `{ id }` no prop `cobranca`.
   */
  const handleResendToProvider = async () => {
    if (hasSynced || resending) return;
    const cobrancaId = (r as any)?.id || cobranca?.id;
    if (!cobrancaId) {
      toast.error('Aguarde os detalhes da cobrança terminarem de carregar.');
      return;
    }
    setResending(true);
    try {
      const { data, error } = await supabase.functions.invoke('financial-charge', {
        body: {
          action: 'resend_to_provider',
          cobranca_id: cobrancaId,
        },
      });

      // Extrair erro real do body quando a edge function retorna status != 2xx
      if (error) {
        let realErrorMsg = 'Erro ao enviar ao provider';
        try {
          const errorBody = await (error as any).context?.json?.();
          realErrorMsg = errorBody?.error || error.message || realErrorMsg;
        } catch {
          realErrorMsg = error.message || realErrorMsg;
        }
        queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
        queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
        toast.error(realErrorMsg);
        return;
      }

      if (data?.success) {
        toast.success('Cobrança enviada ao provider com sucesso!');
        queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
        queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
        onOpenChange(false);
      } else {
        const errorMsg = data?.error || 'Erro desconhecido ao enviar ao provider';
        queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
        queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
        toast.error(errorMsg);
      }
    } catch (err) {
      logger.error('Erro ao reenviar ao provider', { error: err });
      const catchMsg = err instanceof Error ? err.message : 'Erro de rede ao enviar ao provider';
      queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
      queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
      toast.error(catchMsg);
    } finally {
      setResending(false);
    }
  };


  // ── Cálculos dos itens ──
  const subtotalItens = itensCobranca.reduce((acc, item) => acc + (item.valor_unitario * item.quantidade), 0);
  const totalDescontos = itensCobranca.reduce((acc, item) => acc + (item.desconto || 0), 0);

  // ── Crédito aplicado (transparência pro-rata / downgrade) ──
  // Quando o valor_total persistido é menor que (subtotal - descontos), a diferença
  // representa crédito do cliente consumido nesta cobrança (ex: ajuste pro-rata,
  // downgrade de plano, estorno anterior). Exibimos a linha somente quando a
  // diferença é material (> 1 centavo) para evitar ruído de arredondamento.
  // Fonte da verdade do backend: r.pro_rata_metadata.creditos_consumidos quando disponível.
  const valorTotalPersistido = (r as any)?.valor_total ?? 0;
  const creditoDerivado = Math.round(((subtotalItens - totalDescontos) - valorTotalPersistido) * 100) / 100;
  const creditoMetadata = Number((r as any)?.pro_rata_metadata?.creditos_consumidos ?? 0) || 0;
  const creditoAplicado = creditoDerivado > 0.01
    ? (creditoMetadata > 0.01 ? creditoMetadata : creditoDerivado)
    : 0;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Layout expandido — mesmo padrão do CreateChargeModal */}
        <DialogContent className="w-full h-full sm:max-w-5xl sm:max-h-[90vh] sm:h-auto rounded-none sm:rounded-lg flex flex-col p-0 gap-0 overflow-hidden" hideCloseButton>

          {/* ══════════ Header — padrão CreateChargeModal ══════════ */}
          <DialogHeader className="p-3 sm:p-6 pb-3 sm:pb-4 border-b shrink-0">
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full">
              {/* Grupo esquerdo — ícone, título, valor e ações do provider */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 flex-1">
                <div className="p-2.5 rounded-xl bg-primary/10">
                  <Receipt className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-base font-semibold">Detalhes da Cobrança</DialogTitle>
                  {/* ID e Nº Fatura empilhados (um abaixo do outro), sem badge/borda, para liberar espaço horizontal e evitar quebra de linha das ações do header */}
                  <div className="flex flex-col gap-0.5 mt-1">
                    {r?.id && (
                      <span className="font-mono text-[10px] text-muted-foreground leading-tight">
                        ID: {r.id.substring(0, 8).toUpperCase()}
                      </span>
                    )}
                    {parcelas?.[0]?.numero_fatura && (
                      <span className="font-mono text-[10px] text-muted-foreground leading-tight">
                        Nº Fatura: {parcelas[0].numero_fatura}
                      </span>
                    )}
                  </div>
                  {/* Descrição movida para seção própria acima de "Cliente" para evitar quebra do header com textos longos. Mantemos DialogDescription sr-only por acessibilidade (exigido pelo Radix). */}
                  <DialogDescription className="sr-only">
                    Detalhes completos da cobrança
                  </DialogDescription>
                </div>

                {/* Separador vertical entre título e valor/ações — oculto no mobile */}
                <Separator orientation="vertical" className="h-8 mx-1 hidden sm:block" />

                {/* Valor total e tipo de cobrança */}
                <p className="text-lg font-bold text-foreground whitespace-nowrap shrink-0">
                  {r?.valor_total != null ? formatCurrency(r.valor_total, (r as any).moeda) : '—'}
                </p>
                {(r as any)?.tipo_cobranca && (
                  <Badge variant="outline" className="text-[10px] font-medium shrink-0">
                    {tipoLabels[(r as any).tipo_cobranca as TipoCobranca] || (r as any).tipo_cobranca}
                  </Badge>
                )}
                {/* v8.1.0 (2026-09-12): média calculada das parcelas ATIVAS — a edição manual
                    de parcela (conta sem integração) pode gerar valores diferentes; quando
                    divergem, exibe intervalo min–max em vez de média incorreta */}
                {(r as any)?.tipo_cobranca === 'parcelada' && (r as any)?.numero_parcelas > 0 && (() => {
                  const ativas = (parcelas ?? []).filter((p: any) => !['cancelado', 'estornado'].includes(p.status));
                  if (ativas.length === 0) return null;
                  const valores = ativas.map((p: any) => Number(p.valor));
                  const min = Math.min(...valores);
                  const max = Math.max(...valores);
                  const uniformes = Math.abs(max - min) < 0.005;
                  return (
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      {uniformes
                        ? `${ativas.length}x de ${formatCurrency(min, (r as any).moeda)}`
                        : `${ativas.length}x entre ${formatCurrency(min, (r as any).moeda)} e ${formatCurrency(max, (r as any).moeda)}`}
                    </span>
                  );
                })()}

                {/* Botões de ação do provider (PIX, boleto, link, recibo) */}
                {hasSynced && r?.provider_charge_id && (() => {
                  // v7.3.0 — Recibo no header: somente para cobranças únicas pagas.
                  // Parceladas mantêm o recibo na coluna "Recibo" da tabela de parcelas
                  // (cada parcela tem seu próprio link nativo do provider).
                  const isParceladaHeader = (r as any)?.tipo_cobranca === 'parcelada';
                  const headerReceiptUrl = !isParceladaHeader && resolvedStatus === 'pago'
                    ? ((parcelas?.[0]?.metadata as Record<string, unknown> | null)?.['transaction_receipt_url'] as string | undefined)
                        ?? ((cobranca as any)?.metadata?.transaction_receipt_url as string | undefined)
                        ?? null
                    : null;
                  return (
                    <ChargeProviderActions
                      providerChargeId={r.provider_charge_id}
                      contaCorrenteId={(r as any).conta_corrente_id || cobranca?.conta_corrente_id || ''}
                      providerInvoiceUrl={rpcCobranca?.provider_invoice_url || cobranca?.provider_invoice_url}
                      providerPaymentLink={rpcCobranca?.provider_payment_link || cobranca?.provider_payment_link}
                      providerSlug={(rpcCobranca?.provider_slug || cobranca?.provider_slug) ?? undefined}
                      status={resolvedStatus || 'pendente'}
                      billingType={meioPagamento?.tipo_pagamento || (cobranca as any)?.tipo_pagamento}
                      tipoCobranca={(r as any).tipo_cobranca || cobranca?.tipo_cobranca}
                      checkoutMode={checkoutMode}
                      checkoutSessionSlug={headerSlug}
                      receiptUrl={headerReceiptUrl}
                    />
                  );
                })()}
              </div>

              {/* Grupo direito — WhatsApp, status e fechar (alinhado à direita) */}
              <div className="w-full sm:w-auto sm:ml-auto flex items-center justify-end gap-2 shrink-0">
                {paymentLink && (
                  <Button
                    variant="outline"
                    size="xs"
                    className="gap-1.5 shrink-0 text-green-600 border-green-200/60 hover:bg-green-50 dark:border-green-800/40 dark:hover:bg-green-900/20"
                    onClick={handleSendWhatsApp}
                    title={headerUsedFallback ? 'Usando link de contingência (preferido indisponível)' : 'Enviar link de pagamento via WhatsApp'}
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </Button>
                )}
                {/* v1.1.2: botão "Gerar link" removido — o slug do checkout transparente
                    é gerado proativamente pelo trigger trg_ensure_checkout_session toda
                    vez que o provider devolve um link nativo. Para cobranças parceladas,
                    todas as parcelas compartilham o mesmo slug (link único do pai);
                    cobranças avulsas possuem um slug por cobrança. */}
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium shrink-0 ${status.className}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${status.dotClass}`} />
                  {status.label}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0 rounded-lg"
                  onClick={() => onOpenChange(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          {/* ══════════ Corpo com scroll ══════════ */}
          <ScrollArea className="flex-1 overflow-auto">
            <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">

              {/* Loading state global */}
              {loadingDetails && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {!loadingDetails && (
                <>
                  {/* Banner de cancelamento em processamento / falhado (v9.3.0) */}
                  {(() => {
                    const inProgress = resolvedStatus === 'processando_cancelamento';
                    const failed = resolvedStatus === 'falha_cancelamento';
                    if (!inProgress && !failed) return null;
                    const cancelError = (r as any)?.metadata?.cancel_error as string | undefined;
                    return (
                      <div className={`flex items-start gap-3 rounded-lg border-l-4 p-3 ${
                        failed
                          ? 'border-red-500 bg-red-50/60 dark:bg-red-950/20'
                          : 'border-amber-500 bg-amber-50/60 dark:bg-amber-950/20'
                      }`}>
                        <div className={`mt-0.5 h-2 w-2 rounded-full ${failed ? 'bg-red-500' : 'bg-amber-500 animate-pulse'}`} />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${failed ? 'text-red-700 dark:text-red-300' : 'text-amber-700 dark:text-amber-300'}`}>
                            {failed ? 'Cancelamento não confirmado pelo provedor' : 'Processando cancelamento no provedor'}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {failed
                              ? (cancelError || 'O cancelamento local já foi aplicado, mas o provedor recusou ou não respondeu. Tente reenviar.')
                              : 'Aguardando confirmação do provedor. A página será atualizada automaticamente.'}
                          </p>
                        </div>
                        {failed && r?.id && (
                          <RetryCancellationButton cobrancaId={r.id} />
                        )}
                      </div>
                    );
                  })()}

                  {/* Alerta de erro de sincronização ou não-sincronizado */}
                  {(() => {
                    // Busca o último evento com falha e compara com último sucesso
                    // v1.2.1: Só exibe banner se o erro for mais recente que o último sucesso
                    const tEvents = details?.timeline || [];
                    const sortedDesc = [...tEvents].sort(
                      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                    );
                    const lastError = sortedDesc.find(
                      e => e.processing_status === 'failed' && e.error_message
                    );
                    const lastSuccess = sortedDesc.find(
                      e => e.processing_status === 'processed'
                    );
                    // Só mostra erro se ele for mais recente que o último sucesso
                    const showError = lastError && (
                      !lastSuccess ||
                      new Date(lastError.created_at) > new Date(lastSuccess.created_at)
                    );

                    if (showError && lastError?.error_message) {
                      // Banner vermelho com erro real
                      return (
                        <div className="flex items-start gap-2.5 p-3 rounded-xl bg-destructive/5 border border-destructive/20">
                          <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                          <div className="flex flex-col gap-0.5 min-w-0">
                            <span className="text-xs font-medium text-destructive">Erro na sincronização</span>
                            <span className="text-[11px] text-destructive/80 break-words">{lastError.error_message}</span>
                          </div>
                        </div>
                      );
                    }
                    
                    // v9.5.0 — conta sem integração: não há provedor para sincronizar
                    if (isContaManual) {
                      return (
                        <div className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/40 border border-border/60">
                          <AlertTriangle className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          <span className="text-xs text-muted-foreground">
                            Conta sem integração — controle interno. A baixa deve ser registrada manualmente.
                          </span>
                        </div>
                      );
                    }

                    if (!hasSynced) {
                      // v1.x — Motivo real gravado pelo worker ao mover a mensagem para a DLQ
                      // (ex.: "Cliente sem CPF cadastrado"). Sem isso o usuário só via
                      // o aviso genérico e não sabia o que corrigir.
                      const syncError = (r as any)?.metadata?.sync_error as
                        | { message?: string; failed_at?: string }
                        | undefined;

                      if (syncError?.message) {
                        return (
                          <div className="flex items-start gap-2.5 p-3 rounded-xl bg-destructive/5 border border-destructive/20">
                            <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
                            <div className="flex flex-col gap-0.5 min-w-0">
                              <span className="text-xs font-medium text-destructive">
                                Falha ao gerar a cobrança no provider
                              </span>
                              <span className="text-[11px] text-destructive/80 break-words">
                                {syncError.message}
                              </span>
                              <span className="text-[11px] text-destructive/60">
                                Corrija o cadastro e use "Reenviar ao Provider".
                              </span>
                            </div>
                          </div>
                        );
                      }

                      // Banner amarelo genérico (sem erro específico)
                      return (
                        <div className="flex items-center gap-2.5 p-3 rounded-xl bg-yellow-50/80 dark:bg-yellow-900/10 border border-yellow-200/60 dark:border-yellow-800/40">
                          <AlertTriangle className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400 shrink-0" />
                          <span className="text-xs text-yellow-700 dark:text-yellow-400">
                            Cobrança não sincronizada com o provider. Link de pagamento indisponível.
                          </span>
                        </div>
                      );
                    }

                    
                    return null;
                  })()}

                  {/* ── Banner de vínculo com Negócio ── */}
                  {rpcCobranca?.deal_id && (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setDealModalOpen(true)}
                      onKeyDown={(e) => e.key === 'Enter' && setDealModalOpen(true)}
                      className="flex items-center justify-between p-3 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors group"
                    >
                      <div className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                          <Briefcase className="w-3.5 h-3.5 text-primary" />
                        </div>
                        <div className="flex flex-col">
                          <span className="text-xs font-semibold text-foreground">Vinculada ao Negócio</span>
                          <span className="text-[10px] font-mono text-muted-foreground">
                            {formatDealCode(rpcCobranca.deal_id)}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                    </div>
                  )}

                  {/* ── Banner de vínculo com Tarefa (Retenção/Jurídico/Normal) ── */}
                  {(rpcCobranca as any)?.task_id && (() => {
                    // Bloco `task` retornado pela RPC get_cobranca_details (v1.6.0)
                    const task = (details as any)?.task || {};
                    const tipo = String(task.task_type || 'normal');
                    const label =
                      tipo === 'retention' || tipo === 'retencao'
                        ? 'Vinculada à Retenção'
                        : tipo === 'legal' || tipo === 'juridico'
                        ? 'Vinculada ao Jurídico'
                        : 'Vinculada à Tarefa';
                    // Subtítulo: protocolo/número + título, com fallback no ID curto
                    const numero = task.number ? `#${task.number}` : formatDealCode((rpcCobranca as any).task_id);
                    const subtitulo = task.title ? `${numero} · ${task.title}` : numero;

                    return (
                      <div
                        role="button"
                        tabIndex={0}
                        onClick={() => setTaskModalOpen(true)}
                        onKeyDown={(e) => e.key === 'Enter' && setTaskModalOpen(true)}
                        className="flex items-center justify-between p-3 rounded-xl border border-primary/20 bg-primary/5 hover:bg-primary/10 cursor-pointer transition-colors group"
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                            <ClipboardList className="w-3.5 h-3.5 text-primary" />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-xs font-semibold text-foreground">{label}</span>
                            <span className="text-[10px] font-mono text-muted-foreground truncate max-w-[320px]">
                              {subtitulo}
                            </span>
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                      </div>
                    );
                  })()}



                  {/* ═══ SEÇÃO 1.4: KPIs do parcelamento ═══ */}
                  {/* Exibido apenas para cobranças parceladas. Calcula agregados a partir
                      do array `parcelas` já retornado pela RPC get_cobranca_details — sem
                      necessidade de novo round-trip ao backend. */}
                  {((r as any)?.tipo_cobranca || cobranca?.tipo_cobranca) === 'parcelada' && parcelas.length > 0 && (() => {
                    const totalParcelas = parcelas.length;
                    const pagas = parcelas.filter(p => p.status === 'pago');
                    const vencidas = parcelas.filter(p => p.status === 'vencido');
                    const valorTotal = parcelas.reduce((s, p) => s + (Number(p.valor) || 0), 0);
                    const valorRecebido = pagas.reduce((s, p) => s + (Number(p.valor_pago ?? p.valor) || 0), 0);
                    const valorVencido = vencidas.reduce((s, p) => s + (Number(p.valor) || 0), 0);
                    const valorRestante = Math.max(0, valorTotal - valorRecebido);
                    const progresso = totalParcelas > 0 ? Math.round((pagas.length / totalParcelas) * 100) : 0;
                    const numParcelas = (r as any)?.numero_parcelas || totalParcelas;
                    const valorParcela = totalParcelas > 0 ? valorTotal / totalParcelas : 0;
                    const hasVencidas = vencidas.length > 0;

                    return (
                      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                        {/* Valor total da compra */}
                        <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <DollarSign className="h-3.5 w-3.5 text-primary/70" />
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Valor total
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-foreground font-mono">{formatCurrency(valorTotal, (r as any)?.moeda)}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {numParcelas}x de {formatCurrency(valorParcela, (r as any)?.moeda)}
                          </p>
                        </div>

                        {/* Parcelas pagas */}
                        <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <Receipt className="h-3.5 w-3.5 text-primary/70" />
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Parcelas pagas
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-foreground">
                            {pagas.length}
                            <span className="text-xs text-muted-foreground ml-0.5">/{totalParcelas}</span>
                          </p>
                        </div>

                        {/* Recebido */}
                        <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="h-3.5 w-3.5 text-primary/70" />
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                              Recebido
                            </span>
                          </div>
                          <p className="text-sm font-semibold text-foreground font-mono">{formatCurrency(valorRecebido, (r as any)?.moeda)}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {valorRestante > 0 ? `Falta ${formatCurrency(valorRestante, (r as any)?.moeda)}` : 'Quitado'}
                          </p>
                        </div>

                        {/* Vencidas — destaque vermelho quando > 0 */}
                        <div
                          className={`border rounded-lg p-3 space-y-1.5 ${
                            hasVencidas
                              ? 'bg-destructive/5 border-destructive/30'
                              : 'bg-muted/30 border-border/30'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <AlertTriangle
                              className={`h-3.5 w-3.5 ${hasVencidas ? 'text-destructive' : 'text-primary/70'}`}
                            />
                            <span
                              className={`text-[10px] uppercase tracking-wider font-semibold ${
                                hasVencidas ? 'text-destructive' : 'text-muted-foreground'
                              }`}
                            >
                              Vencidas
                            </span>
                          </div>
                          <p
                            className={`text-sm font-semibold ${
                              hasVencidas ? 'text-destructive' : 'text-foreground'
                            }`}
                          >
                            {vencidas.length}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            {hasVencidas
                              ? `${formatCurrency(valorVencido, (r as any)?.moeda)} em atraso`
                              : 'Sem atrasos'}
                          </p>
                        </div>
                      </section>
                    );
                  })()}

                  {/* ═══ SEÇÃO 1.5: Descrição da Cobrança ═══ */}
                  {/* Renderiza apenas quando há descrição. Texto com quebra natural para suportar descrições longas sem desconfigurar o layout. */}
                  {(r?.descricao || cobranca?.descricao) && (
                    <section>
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Descrição</h3>
                      </div>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap break-words leading-relaxed">
                        {r?.descricao || cobranca?.descricao}
                      </p>
                    </section>
                  )}


                  {/* ═══ SEÇÃO 2: Cliente ═══ */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <User className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Cliente</h3>
                      {/* v7.5.0 — seta padrão (igual à seção Pagador do modal de recorrência):
                          sinaliza que a ficha completa do cliente pode ser aberta daqui */}
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 ml-1"
                              disabled={!chargeCustomerId}
                              onClick={() => setCustomerModalOpen(true)}
                              aria-label="Abrir cadastro do cliente"
                            >
                              <ArrowUpRight className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Abrir cadastro do cliente</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>

                    {customerName ? (
                      // Layout compacto: avatar + nome à esquerda; Email/Doc/Telefone na MESMA linha à direita
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
                        {/* Avatar + Nome + Tipo */}
                        <div className="flex items-center gap-3 sm:min-w-[220px] sm:shrink-0">
                          {/* v7.4.0: avatar + nome abrem a ficha do cliente quando há cliente vinculado */}
                          <button
                            type="button"
                            disabled={!chargeCustomerId}
                            onClick={() => setCustomerModalOpen(true)}
                            className="shrink-0 rounded-full disabled:cursor-default"
                            aria-label={`Abrir ficha do cliente ${customerName}`}
                          >
                            <SmartAvatar name={customerName} size="md" />
                          </button>
                          <div className="min-w-0">
                            {chargeCustomerId ? (
                              <button
                                type="button"
                                onClick={() => setCustomerModalOpen(true)}
                                className="block max-w-full text-left text-sm font-semibold text-foreground truncate hover:underline hover:text-primary focus-visible:outline-none focus-visible:underline"
                                title={`Abrir ficha de ${customerName}`}
                              >
                                {customerName}
                              </button>
                            ) : (
                              <p className="text-sm font-semibold text-foreground truncate" title={customerName}>{customerName}</p>
                            )}
                            {customerData?.customer_type && (
                              <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4 mt-0.5">
                                {customerData.customer_type === 'pj' ? 'Pessoa Jurídica' : 'Pessoa Física'}
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Email / Documento / Telefone — em linha ao lado do nome */}
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 flex-1 min-w-0">
                          <div className="min-w-0">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Email</span>
                            <p className="text-xs text-foreground truncate" title={customerData?.email || ''}>
                              {customerData?.email || '—'}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
                              {customerData?.customer_type === 'pj' ? 'CNPJ' : 'CPF'}
                            </span>
                            <p className="text-xs text-foreground truncate">
                              {customerData?.customer_type === 'pj'
                                ? (formatDocumentForDisplay(customerData?.cnpj) || '—')
                                : (formatDocumentForDisplay(customerData?.cpf) || '—')}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Telefone</span>
                            <p className="text-xs text-foreground truncate">
                              {customerData?.phone
                                ? formatPhoneForDisplay(customerData.phone)
                                : '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">Nenhum cliente vinculado</p>
                    )}
                  </section>

                  <Separator />

                  {/* ═══ SEÇÃO 3: Itens da Cobrança ═══ */}
                  <section>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <ShoppingCart className="h-4 w-4 text-primary" />
                        <h3 className="text-sm font-semibold text-foreground">Itens da Cobrança</h3>
                      </div>
                      {/* v7.3.0: Botão para abrir a memória de cálculo (apenas se cobrança veio de assinatura) */}
                      {((r as any)?.assinatura_id) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 gap-1.5 text-xs"
                          onClick={() => setMemoriaOpen(true)}
                          title="Ver memória de cálculo desta cobrança"
                        >
                          <Calculator className="h-3.5 w-3.5" />
                          Memória de cálculo
                        </Button>
                      )}
                    </div>
                    {itensCobranca.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-4 text-muted-foreground">
                        <Package className="h-5 w-5 mb-1.5 opacity-40" />
                        <span className="text-xs">Nenhum item registrado</span>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Cabeçalho da tabela — oculto no mobile */}
                        <div className="hidden sm:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 pb-1 border-b border-border/30">
                          <span className="col-span-5">Produto</span>
                          <span className="col-span-1 text-center">Qtd</span>
                          <span className="col-span-2 text-right">Unitário</span>
                          <span className="col-span-2 text-right">Desconto</span>
                          <span className="col-span-2 text-right">Total</span>
                        </div>

                        {/* Linhas dos itens — layout card no mobile, grid no desktop */}
                        {itensCobranca.map((item) => {
                          const itemTotal = item.total || (item.valor_unitario * item.quantidade - (item.desconto || 0));
                          return (
                            <div key={item.id} className="rounded-lg bg-muted/30 border border-border/20 text-xs">
                              {/* Desktop: grid horizontal */}
                              <div className="hidden sm:grid grid-cols-12 gap-2 items-center px-2 py-1.5">
                                <span className="col-span-5 font-medium text-foreground truncate" title={item.product_name || item.descricao || '—'}>
                                  {item.product_name || item.descricao || '—'}
                                </span>
                                <span className="col-span-1 text-center text-muted-foreground">{item.quantidade}</span>
                                <span className="col-span-2 text-right text-foreground">{formatCurrency(item.valor_unitario)}</span>
                                <span className="col-span-2 text-right">
                                  {(item.desconto && item.desconto > 0) ? (
                                    <span className="text-destructive flex items-center justify-end gap-0.5">-{formatCurrency(item.desconto)}</span>
                                  ) : (
                                    <span className="text-muted-foreground/50">—</span>
                                  )}
                                </span>
                                <span className="col-span-2 text-right font-semibold text-foreground">{formatCurrency(itemTotal)}</span>
                              </div>
                              {/* Mobile: layout empilhado */}
                              <div className="sm:hidden px-2.5 py-2 space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-foreground truncate flex-1 mr-2" title={item.product_name || item.descricao || '—'}>
                                    {item.product_name || item.descricao || '—'}
                                  </span>
                                  <span className="font-semibold text-foreground shrink-0">{formatCurrency(itemTotal)}</span>
                                </div>
                                <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                                  <span>Qtd: {item.quantidade}</span>
                                  <span>Unit: {formatCurrency(item.valor_unitario)}</span>
                                  {(item.desconto && item.desconto > 0) && (
                                    <span className="text-destructive">Desc: -{formatCurrency(item.desconto)}</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Rodapé com totais */}
                        <div className="border-t border-border/40 pt-2 mt-1 space-y-1 px-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Subtotal</span>
                            <span className="font-medium text-foreground">{formatCurrency(subtotalItens)}</span>
                          </div>
                          {totalDescontos > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-muted-foreground">Descontos</span>
                              <span className="font-medium text-destructive">-{formatCurrency(totalDescontos)}</span>
                            </div>
                          )}
                          {/* Crédito aplicado: diferença entre (subtotal - descontos) e o valor_total persistido.
                              Indica saldo de crédito do cliente consumido nesta cobrança (pro-rata/downgrade). */}
                          {creditoAplicado > 0 && (
                            <div className="flex items-center justify-between text-xs">
                              <span
                                className="text-muted-foreground inline-flex items-center gap-1"
                                title={`Crédito do cliente consumido nesta cobrança (saldo de ajuste pro-rata ou downgrade anterior). ${formatCurrency(subtotalItens - totalDescontos)} − ${formatCurrency(creditoAplicado)} = ${formatCurrency(valorTotalPersistido, (r as any)?.moeda)}.`}
                              >
                                Crédito aplicado
                                <Info className="h-3 w-3 opacity-60" />
                              </span>
                              <span className="font-medium text-emerald-600 dark:text-emerald-500">-{formatCurrency(creditoAplicado)}</span>
                            </div>
                          )}
                          <div className="flex items-center justify-between text-sm font-bold pt-1 border-t border-border/30">
                            <span className="text-foreground">Total</span>
                            <span className="text-foreground">{r?.valor_total != null ? formatCurrency(r.valor_total, (r as any).moeda) : '—'}</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </section>

                  <Separator />

                  {/* ═══ SEÇÃO 4: Detalhes (Emissão, Vencimento, Pagamento) ═══ */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <FileText className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Detalhes</h3>
                    </div>
                    {(() => {
                      // Deriva informações de cancelamento a partir da primeira parcela cancelada
                      const cancelParcela = parcelas.find((p: any) => p.cancelled_at);
                      const statusAtual = (rpcCobranca?.status || cobranca?.status) as string | undefined;
                      const isCancelado = statusAtual === 'cancelado' && !!cancelParcela;
                      return (
                        <>
                          <div className={`grid grid-cols-1 ${isCancelado ? 'sm:grid-cols-4' : 'sm:grid-cols-3'} gap-3 sm:gap-4`}>
                            {/* Emissão */}
                            <div className="space-y-1">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Emissão</p>
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5 text-muted-foreground/60" />
                                <span className="text-sm font-medium text-foreground">
                                  {safeFormatDate(rpcCobranca?.data_emissao || cobranca?.data_emissao)}
                                </span>
                              </div>
                            </div>
                            {/* Vencimento */}
                            <div className="space-y-1">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Vencimento</p>
                              <div className="flex items-center gap-1.5">
                                <Calendar className="h-3.5 w-3.5 text-muted-foreground/60" />
                                <span className="text-sm font-medium text-foreground">
                                  {safeFormatDate(rpcCobranca?.data_vencimento || cobranca?.data_vencimento)}
                                </span>
                              </div>
                            </div>
                            {/* Data de Pagamento */}
                            <div className="space-y-1">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Pagamento</p>
                              <div className="flex items-center gap-1.5">
                                {(() => {
                                  const dataPgto = (rpcCobranca as any)?.primeira_data_pagamento || (cobranca as any)?.primeira_data_pagamento;
                                  if (dataPgto) {
                                    return (
                                      <>
                                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                                        <span className="text-sm font-medium text-green-600 dark:text-green-400">
                                          {safeFormatDate(dataPgto)}
                                        </span>
                                      </>
                                    );
                                  }
                                  return (
                                    <>
                                      <Calendar className="h-3.5 w-3.5 text-muted-foreground/40" />
                                      <span className="text-sm text-muted-foreground">—</span>
                                    </>
                                  );
                                })()}
                              </div>
                            </div>
                            {/* Cancelamento — exibido apenas quando a cobrança está cancelada */}
                            {isCancelado && (
                              <div
                                className="space-y-1"
                                title={cancelParcela?.cancelled_by_user_name ? `Cancelado por: ${cancelParcela.cancelled_by_user_name}` : undefined}
                              >
                                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Cancelamento</p>
                                <div className="flex items-center gap-1.5">
                                  <XCircle className="h-3.5 w-3.5 text-destructive" />
                                  <span className="text-sm font-medium text-destructive">
                                    {safeFormatDate(cancelParcela!.cancelled_at)}
                                  </span>
                                </div>
                                {cancelParcela?.cancelled_by_user_name && (
                                  <p className="text-[11px] text-muted-foreground truncate">
                                    por {cancelParcela.cancelled_by_user_name}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                          {/* Motivo do cancelamento */}
                          {isCancelado && cancelParcela?.cancel_reason && (
                            <div className="mt-3 pt-3 border-t border-border/30">
                              <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                Motivo do cancelamento
                              </p>
                              <p className="text-sm text-foreground/80 bg-destructive/5 border border-destructive/20 p-2.5 rounded-lg leading-relaxed">
                                {cancelParcela.cancel_reason}
                              </p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                    {/* Observações */}
                    {(rpcCobranca?.observacoes || cobranca?.observacoes) && (
                      <div className="mt-3 pt-3 border-t border-border/30">
                        <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">Observações</p>
                        <p className="text-sm text-foreground/80 bg-muted/40 p-2.5 rounded-lg leading-relaxed">
                          {rpcCobranca?.observacoes || cobranca?.observacoes}
                        </p>
                      </div>
                    )}
                  </section>

                  {/* Veículos vinculados (módulo Veículos) */}
                  <FinanceiroVehiclesBlock cobrancaId={cobranca?.id ?? null} enabled={open} className="pt-1" />

                  <Separator />

                  {/* ═══ SEÇÃO 5: Pagamento & Conta ═══ */}
                  {/* v7.3.1 — Restaurado leiaute em cards (padronizado também no modal de detalhes da recorrência). */}
                  {(meioPagamento || contaCorrente) && (
                    <>
                      <section>
                        <div className="flex items-center gap-2 mb-3">
                          <Landmark className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Pagamento & Conta</h3>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Coluna: Meio de Pagamento */}
                          {meioPagamento && (
                            <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-2">
                              <div className="flex items-center gap-2 mb-2">
                                <CreditCard className="h-3.5 w-3.5 text-primary/70" />
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Meio de Pagamento</span>
                              </div>
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted-foreground">Nome</span>
                                  <span className="text-xs font-medium text-foreground">{meioPagamento.nome}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted-foreground">Tipo</span>
                                  <Badge variant="outline" className="text-[10px] font-medium flex items-center gap-1">
                                    {(() => {
                                      const Icon = PaymentMethodIcon[meioPagamento.tipo_pagamento] || DefaultPaymentIcon;
                                      return <Icon className="h-3 w-3" />;
                                    })()}
                                    {tipoPagamentoLabels[meioPagamento.tipo_pagamento] || meioPagamento.tipo_pagamento}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Coluna: Conta Corrente */}
                          {contaCorrente && (
                            <div className="bg-muted/30 border border-border/30 rounded-lg p-3 space-y-2">
                              <div className="flex items-center gap-2 mb-2">
                                <Landmark className="h-3.5 w-3.5 text-primary/70" />
                                <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Conta Corrente</span>
                              </div>
                              <div className="space-y-1.5">
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted-foreground">Nome</span>
                                  <span className="text-xs font-medium text-foreground">{contaCorrente.nome}</span>
                                </div>
                                {contaCorrente.banco_nome && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-muted-foreground">Banco</span>
                                    <span className="text-xs text-foreground">{contaCorrente.banco_nome}</span>
                                  </div>
                                )}
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] text-muted-foreground">Tipo</span>
                                  <span className="text-xs text-foreground capitalize">{contaCorrente.tipo_conta}</span>
                                </div>
                                {contaCorrente.provider_slug && (
                                  <div className="flex items-center justify-between">
                                    <span className="text-[11px] text-muted-foreground">Provider</span>
                                    <Badge variant="secondary" className="text-[10px] font-medium capitalize">
                                      {contaCorrente.provider_slug}
                                    </Badge>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                      <Separator />
                    </>
                  )}


                  {/* ═══ SEÇÃO 5.1: Juros/Multa + Expiração (read-only) ═══ */}
                  {/* v7.4.0 — Paridade entre cobrança avulsa, parcelada e assinatura:
                     prioriza juros/dias da assinatura quando existe (recorrência); caso
                     contrário, lê direto da cobrança (avulsa/parcelada — colunas
                     financeiro_cobrancas.juros e dias_expiracao_pos_vencimento). */}
                  {(() => {
                    const assin = details?.assinatura as any;
                    const cob = (rpcCobranca || cobranca) as any;
                    const jSrc = assin?.juros ?? cob?.juros;
                    const diasSrc = assin?.dias_expiracao_pos_vencimento ?? cob?.dias_expiracao_pos_vencimento;
                    if (!jSrc && diasSrc == null) return null;
                    const j = jSrc || {};
                    const jurosValue = {
                      enabled: Boolean(j?.enabled),
                      multa_tipo: (j?.multa_tipo as 'percent' | 'fixed') || 'percent',
                      multa_valor: Number(j?.multa_valor) || 0,
                      juros_tipo: (j?.juros_tipo as 'percent' | 'fixed') || 'percent',
                      juros_valor: Number(j?.juros_valor) || 0,
                    };
                    const dias = Number(diasSrc) || 60;
                    return (
                      <>
                        <section className="space-y-3">
                          <SubscriptionJurosSection value={jurosValue} readOnly defaultOpen={false} />
                          {/* v9.5.0 — expiração só existe para cobrança emitida em provedor */}
                          {!isContaManual && (
                            <SubscriptionExpiracaoSection value={dias} readOnly defaultOpen={false} />
                          )}
                        </section>
                        <Separator />
                      </>
                    );
                  })()}


                  {/* ═══ SEÇÃO 5.5: Comprovantes de Pagamento (v9.1.0) ═══ */}
                  {/* Exibe os anexos de baixa (manual ou sistema) independente do tipo de cobrança */}
                  {parcelas.some(p => (p as { paid_receipt_path?: string | null }).paid_receipt_path) && (
                    <>
                      <section>
                        <div className="flex items-center gap-2 mb-3">
                          <Paperclip className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Comprovantes de Pagamento</h3>
                        </div>
                        <div className="space-y-2">
                          {parcelas
                            .filter(p => (p as { paid_receipt_path?: string | null }).paid_receipt_path)
                            .map(p => {
                              const paidReceiptPath = (p as { paid_receipt_path?: string | null }).paid_receipt_path as string;
                              const paidSource = (p as { paid_source?: 'manual' | 'provider_webhook' | 'system' | null }).paid_source || null;
                              const paidSourceLabel = paidSource === 'manual' ? 'Manual' : (paidSource === 'provider_webhook' || paidSource === 'system') ? 'Sistema' : null;
                              const ext = paidReceiptPath.split('.').pop()?.toLowerCase() || '';
                              const isImage = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
                              return (
                                <div
                                  key={p.id}
                                  className="flex items-center gap-3 rounded-lg border border-border/40 bg-muted/30 p-2.5 hover:bg-muted/50 transition-colors"
                                >
                                  <div className="flex h-9 w-9 items-center justify-center rounded-md bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 shrink-0">
                                    <Paperclip className="h-4 w-4" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-xs font-semibold text-foreground">
                                        Parcela #{p.numero_parcela} — {formatCurrency(p.valor)}
                                      </span>
                                      {paidSourceLabel && (
                                        <span
                                          className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${
                                            paidSource === 'manual'
                                              ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
                                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                          }`}
                                          title={paidSource === 'manual' ? 'Baixa registrada manualmente' : 'Baixa registrada automaticamente pelo provider'}
                                        >
                                          {paidSourceLabel}
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                                      {p.data_pagamento && (
                                        <span>Pago em {safeFormatDate(p.data_pagamento, 'dd/MM/yyyy')}</span>
                                      )}
                                      <span className="uppercase tracking-wider">{isImage ? 'Imagem' : (ext || 'arquivo')}</span>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-8 text-xs shrink-0"
                                    onClick={() => setReceiptLightbox({ path: paidReceiptPath, name: `Comprovante parcela #${p.numero_parcela}` })}
                                  >
                                    <ExternalLink className="h-3.5 w-3.5 mr-1" />
                                    Ver comprovante
                                  </Button>
                                </div>
                              );
                            })}
                        </div>
                      </section>
                      <Separator />
                    </>
                  )}


                  {/* ═══ SEÇÃO 6: Parcelas (condicional) ═══ */}
                  {((r as any)?.tipo_cobranca || cobranca?.tipo_cobranca) === 'parcelada' && (
                    <>
                      <section>
                        <div className="flex items-center gap-2 mb-3">
                          <List className="h-4 w-4 text-primary" />
                          <h3 className="text-sm font-semibold text-foreground">Parcelas</h3>
                        </div>
                        {parcelas.length === 0 ? (
                          <p className="text-xs text-muted-foreground text-center py-2">Nenhuma parcela encontrada</p>
                        ) : (
                          <div className="space-y-1.5">
                            {/* Cabeçalho da tabela de parcelas — oculto no mobile */}
                            <div className="hidden sm:flex items-center text-[10px] uppercase tracking-wider text-muted-foreground px-2.5 pb-1">
                              <span className="w-8">#</span>
                              <span className="w-20">N.Fatura</span>
                              <span className="flex-1">Valor</span>
                              <span className="w-20 text-center">Vencimento</span>
                              <span className="w-20 text-center">Pagamento</span>
                              <span className="w-20 text-center">Status</span>
                              <span className="w-14 text-center">Link</span>
                              <span className="w-14 text-center">Boleto</span>
                              <span className="w-14 text-center">Recibo</span>
                              <span className="w-14 text-center">Compr.</span>
                              <span className="w-20 text-center">Ações</span>
                            </div>

                            {parcelas.map(p => {
                              const pStatus = statusConfig[p.status as FinanceiroStatus] || statusConfig.pendente;
                              // v1.2.0: cada parcela tem SEU PRÓPRIO slug (boleto/PIX
                              // distintos no provider). Usa p.checkout_session_slug se
                              // disponível; fallback para o slug do pai (slugs legados,
                              // antes da migration por parcela).
                              const parcelaSlug =
                                (p as { checkout_session_slug?: string | null }).checkout_session_slug
                                ?? headerSlug;
                              const parcelaResolved = resolveCheckoutUrl({
                                checkout_mode: checkoutMode,
                                checkout_session_slug: parcelaSlug,
                                provider_invoice_url: (p.metadata?.invoice_url as string) ?? null,
                                provider_payment_link: paymentLink,
                              });
                              const parcelaLink = parcelaResolved.url;
                              const parcelaLinkFallback = parcelaResolved.usedFallback;

                              const bankSlipUrl = p.metadata?.bank_slip_url as string;
                              const receiptUrl = p.metadata?.transaction_receipt_url as string;
                              // v9.0.0: comprovante de baixa manual (bucket 'anexos') + origem
                              const paidReceiptPath = (p as { paid_receipt_path?: string | null }).paid_receipt_path || null;
                              const paidSource = (p as { paid_source?: 'manual' | 'provider_webhook' | 'system' | null }).paid_source || null;
                              const paidSourceLabel = paidSource === 'manual' ? 'Manual' : (paidSource === 'provider_webhook' || paidSource === 'system') ? 'Sistema' : null;
                              // v9.1.0: ações editar/cancelar por parcela individual
                              const parcelaProviderPaymentId = (p.metadata?.provider_payment_id as string | undefined) || null;
                              // v9.2.0: bloqueio cartão parcelado — Asaas recalcula saldo do
                              // installment ao cancelar 1 parcela, mas o sistema preserva
                              // valor_total do pai. Para evitar divergência, ações individuais
                              // são bloqueadas quando há 2+ parcelas ativas no cartão.
                              const isCartao = ['cartao_credito', 'cartao_debito'].includes(
                                meioPagamento?.tipo_pagamento ?? ''
                              );
                              const parcelasAtivasCount = (parcelas ?? []).filter(
                                (x) => !['cancelado', 'pago', 'estornado'].includes(x.status)
                              ).length;
                              const blockParcelaActionsCartao = isCartao && parcelasAtivasCount > 1;
                              // v9.6.0: conta SEM integração (controle manual) — não existe
                              // provedor gerenciando o parcelamento, então cada parcela em
                              // aberto pode ser editada e cancelada individualmente.
                              const parcelaStatusAberto = ['aguardando_pagamento', 'vencido', 'edicao_andamento', 'pendente'].includes(p.status);
                              const parcelaManualEditable = isContaManual
                                && effectiveCanEdit
                                && parcelaStatusAberto
                                && !parcelaProviderPaymentId;
                              const parcelaEditable = parcelaManualEditable || (
                                effectiveCanEdit
                                && parcelaStatusAberto
                                && !!parcelaProviderPaymentId
                                && !blockParcelaActionsCartao
                              );
                              // v9.3.0: Cancelamento de parcela individual removido para todos os meios
                              // de pagamento — evita divergência entre cobrança-pai e parcelas filhas.
                              // Cancelamento agora só pelo botão "Cancelar fatura" no topo do modal,
                              // que cancela em cascata todas as parcelas em aberto.
                              // v9.6.0: exceção para conta sem integração (controle 100% interno).
                              const parcelaCancelavel = parcelaManualEditable;
                              // Indicador visual de cartão parcelado bloqueado mantém-se apenas para edição.
                              const showCartaoBlockedHint = blockParcelaActionsCartao
                                && !isContaManual
                                && effectiveCanEdit
                                && ['aguardando_pagamento', 'vencido', 'edicao_andamento', 'pendente', 'aguardando_emissao'].includes(p.status);
                              const cartaoBlockedTitle = 'Parcelamento no cartão: edição individual desabilitada para evitar divergência com o provedor. Use "Cancelar fatura" no topo para cancelar a cobrança inteira.';
                              return (
                                <div key={p.id} className="rounded-lg bg-muted/30 border border-border/30">
                                  {/* Desktop: layout horizontal */}
                                  <div className="hidden sm:flex items-center text-xs p-2.5">
                                    <span className="font-semibold text-foreground w-8">#{p.numero_parcela}</span>
                                    <span className="text-muted-foreground w-20 text-[10px] truncate" title={p.numero_fatura || ''}>
                                      {p.numero_fatura || '—'}
                                    </span>
                                    <span className="font-medium text-foreground flex-1">{formatCurrency(p.valor)}</span>
                                    <span className="text-muted-foreground w-20 text-center">
                                      {safeFormatDate(p.data_vencimento, 'dd/MM/yy')}
                                    </span>
                                    <span className="w-20 text-center">
                                      {p.data_pagamento ? (
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                          {safeFormatDate(p.data_pagamento, 'dd/MM/yy')}
                                        </span>
                                      ) : (
                                        <span className="text-muted-foreground/40">—</span>
                                      )}
                                    </span>
                                    <span className={`inline-flex items-center justify-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium w-20 ${pStatus.className}`}>
                                      <span className={`w-1 h-1 rounded-full ${pStatus.dotClass}`} />
                                      {pStatus.label}
                                    </span>
                                    <span className="w-14 flex justify-center">
                                      {parcelaLink ? (
                                        <a href={parcelaLink} target="_blank" rel="noopener noreferrer"
                                          className="text-primary hover:text-primary/80 transition-colors"
                                          title={parcelaLinkFallback ? 'Link de pagamento (contingência: preferido indisponível)' : 'Link de pagamento'}>
                                          <ExternalLink className="h-3.5 w-3.5" />
                                        </a>
                                      ) : (
                                        <span className="text-muted-foreground/20"><ExternalLink className="h-3.5 w-3.5" /></span>
                                      )}
                                    </span>
                                    <span className="w-14 flex justify-center">
                                      {bankSlipUrl ? (
                                        <a href={bankSlipUrl} target="_blank" rel="noopener noreferrer"
                                          className="text-orange-600 hover:text-orange-500 dark:text-orange-400 dark:hover:text-orange-300 transition-colors" title="Baixar boleto PDF">
                                          <FileText className="h-3.5 w-3.5" />
                                        </a>
                                      ) : (
                                        <span className="text-muted-foreground/20"><FileText className="h-3.5 w-3.5" /></span>
                                      )}
                                    </span>
                                    <span className="w-14 flex justify-center">
                                      {receiptUrl ? (
                                        <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
                                          className="text-green-600 hover:text-green-500 dark:text-green-400 dark:hover:text-green-300 transition-colors" title="Comprovante de recebimento">
                                          <FileCheck className="h-3.5 w-3.5" />
                                        </a>
                                      ) : (
                                        <span className="text-muted-foreground/20"><FileCheck className="h-3.5 w-3.5" /></span>
                                      )}
                                    </span>
                                    {/* v9.0.0: Comprovante de baixa manual — abre lightbox interno */}
                                    <span className="w-14 flex justify-center">
                                      {paidReceiptPath ? (
                                        <button
                                          type="button"
                                          onClick={() => setReceiptLightbox({ path: paidReceiptPath, name: `Comprovante parcela #${p.numero_parcela}` })}
                                          className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors"
                                          title={paidSource === 'manual' ? 'Comprovante anexado na baixa manual' : 'Comprovante anexado'}
                                        >
                                          <Paperclip className="h-3.5 w-3.5" />
                                        </button>
                                      ) : (
                                        <span className="text-muted-foreground/20" title="Sem comprovante anexado"><Paperclip className="h-3.5 w-3.5" /></span>
                                      )}
                                    </span>
                                    {/* v9.1.0: Ações por parcela — editar/cancelar individual */}
                                    <span className="w-20 flex items-center justify-center gap-1">
                                      {parcelaEditable && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            // v9.6.0: conta manual usa o editor local por parcela
                                            if (parcelaManualEditable) {
                                              setEditParcelaManual(p);
                                              setEditParcelaManualOpen(true);
                                              return;
                                            }
                                            setEditParcela(p);
                                            setEditParcelaOpen(true);
                                          }}
                                          className="p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 text-blue-600 dark:text-blue-400 transition-colors"
                                          title="Editar esta parcela"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                      {/* v9.6.0: cancelamento por parcela apenas em conta sem integração */}
                                      {parcelaCancelavel && (
                                        <button
                                          type="button"
                                          onClick={() => setCancelParcelaManual(p)}
                                          className="p-1 rounded hover:bg-destructive/10 text-destructive transition-colors"
                                          title="Cancelar esta parcela"
                                        >
                                          <XCircle className="h-3.5 w-3.5" />
                                        </button>
                                      )}
                                      {!parcelaEditable && (
                                        showCartaoBlockedHint ? (
                                          <span
                                            className="inline-flex items-center justify-center text-muted-foreground/60 cursor-not-allowed"
                                            title={cartaoBlockedTitle}
                                          >
                                            <Lock className="h-3.5 w-3.5" />
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground/20 text-[10px]">—</span>
                                        )
                                      )}
                                    </span>
                                  </div>

                                  {/* Mobile: layout mini-card empilhado */}
                                  <div className="sm:hidden px-2.5 py-2 space-y-1.5 text-xs">
                                    <div className="flex items-center justify-between">
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold text-foreground">#{p.numero_parcela}</span>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${pStatus.className}`}>
                                          <span className={`w-1 h-1 rounded-full ${pStatus.dotClass}`} />
                                          {pStatus.label}
                                        </span>
                                      </div>
                                      <span className="font-semibold text-foreground">{formatCurrency(p.valor)}</span>
                                    </div>
                                    <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                                      <span>Venc: {safeFormatDate(p.data_vencimento, 'dd/MM/yy')}</span>
                                      {p.data_pagamento && (
                                        <span className="text-green-600 dark:text-green-400 font-medium">
                                          Pago: {safeFormatDate(p.data_pagamento, 'dd/MM/yy')}
                                        </span>
                                      )}
                                      {p.numero_fatura && (
                                        <span className="truncate">Fatura: {p.numero_fatura}</span>
                                      )}
                                      {paidSourceLabel && (
                                        <span
                                          className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${paidSource === 'manual' ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' : 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'}`}
                                          title={paidSource === 'manual' ? 'Baixa registrada manualmente' : 'Baixa registrada automaticamente pelo provider'}
                                        >
                                          {paidSourceLabel}
                                        </span>
                                      )}
                                    </div>
                                    {/* Ações rápidas no mobile */}
                                    <div className="flex items-center gap-3 pt-0.5">
                                      {parcelaLink && (
                                        <a href={parcelaLink} target="_blank" rel="noopener noreferrer"
                                          className="text-primary hover:text-primary/80 transition-colors flex items-center gap-1 text-[10px]"
                                          title={parcelaLinkFallback ? 'Contingência: preferido indisponível' : undefined}>
                                          <ExternalLink className="h-3 w-3" /> Link
                                        </a>
                                      )}
                                      {bankSlipUrl && (
                                        <a href={bankSlipUrl} target="_blank" rel="noopener noreferrer"
                                          className="text-orange-600 dark:text-orange-400 transition-colors flex items-center gap-1 text-[10px]">
                                          <FileText className="h-3 w-3" /> Boleto
                                        </a>
                                      )}
                                      {receiptUrl && (
                                        <a href={receiptUrl} target="_blank" rel="noopener noreferrer"
                                          className="text-green-600 dark:text-green-400 transition-colors flex items-center gap-1 text-[10px]">
                                          <FileCheck className="h-3 w-3" /> Recibo
                                        </a>
                                      )}
                                      {paidReceiptPath && (
                                        <button
                                          type="button"
                                          onClick={() => setReceiptLightbox({ path: paidReceiptPath, name: `Comprovante parcela #${p.numero_parcela}` })}
                                          className="text-emerald-600 dark:text-emerald-400 transition-colors flex items-center gap-1 text-[10px]"
                                        >
                                          <Paperclip className="h-3 w-3" /> Compr.
                                        </button>
                                      )}
                                      {/* v9.3.0: Apenas edição individual; cancelamento somente via "Cancelar fatura" no topo */}
                                      {parcelaEditable && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setEditParcela(p);
                                            setEditParcelaOpen(true);
                                          }}
                                          className="ml-auto text-blue-600 dark:text-blue-400 transition-colors flex items-center gap-1 text-[10px]"
                                          title="Editar esta parcela"
                                        >
                                          <Pencil className="h-3 w-3" /> Editar
                                        </button>
                                      )}
                                      {showCartaoBlockedHint && (
                                        <span
                                          className="ml-auto inline-flex items-center gap-1 text-[10px] text-muted-foreground/70"
                                          title={cartaoBlockedTitle}
                                        >
                                          <Lock className="h-3 w-3" /> Bloqueado
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                </div>
                              );
                            })}
                          </div>
                        )}
                      </section>
                      <Separator />
                    </>
                  )}

                  {/* ═══ SEÇÃO 6.5: Cartão usado (Fase 5/C.1) ═══ */}
                  {r?.id && <ChargeCardUsedSection cobrancaId={r.id} />}

                  {/* ═══ SEÇÃO 7: Jornada da Cobrança (Timeline) — altura fixa com scroll interno ═══ */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <History className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Jornada da Cobrança</h3>
                    </div>
                    {/* Container com altura máxima fixa e scroll interno para evitar crescimento excessivo do modal */}
                    <div ref={timelineScrollRef} className="max-h-[280px] overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent">
                      <ChargeTimeline
                        timelineEvents={timelineEvents}
                        providerChargeId={r?.provider_charge_id || cobranca?.provider_charge_id}
                        createdAt={rpcCobranca?.created_at || cobranca?.created_at}
                        parcelas={parcelas?.map(p => ({
                          id: p.id,
                          numero_parcela: p.numero_parcela,
                          metadata: p.metadata,
                        }))}
                        lookups={timelineLookups}
                      />
                    </div>
                  </section>

                  <Separator />

                  {/* ═══ SEÇÃO 7.1: Notificações da Régua de Cobrança (sanfona, lazy) ═══ */}
                  {/* v8.1.0 (2026-09-12): liga/desliga por cobrança via flag server-side */}
                  {cobranca?.id && (
                    <>
                      <ChargeRemindersSection
                        cobrancaId={cobranca.id}
                        remindersEnabled={rpcCobranca?.reminders_enabled ?? true}
                        canUpdate={effectiveCanEdit}
                      />
                      <Separator />
                    </>
                  )}



                  {/* ═══ SEÇÃO 8: Auditoria ═══ */}
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <Shield className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold text-foreground">Auditoria</h3>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Criado por */}
                      <div className="flex items-center gap-2.5">
                        <SmartAvatar
                          src={creatorProfile?.avatar_url}
                          name={creatorProfile?.full_name}
                          size="sm"
                          use3DFallback
                        />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Criado por</p>
                          <p className="text-sm font-medium text-foreground">{creatorProfile?.full_name || '—'}</p>
                        </div>
                      </div>
                      {/* Criado em */}
                      <div className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted/60 border border-border/30">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Criado em</p>
                          <p className="text-sm font-medium text-foreground">
                            {(rpcCobranca?.created_at || cobranca?.created_at)
                              ? format(new Date(rpcCobranca?.created_at || cobranca!.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              )}
            </div>
          </ScrollArea>

          {/* ══════════ Footer ══════════ */}
          <div className="flex flex-wrap items-center justify-end gap-2 px-3 sm:px-6 py-3 sm:py-4 border-t border-border/50 bg-muted/20 shrink-0">
            {/* Feature 1 — quando a recorrência tem cartão padrão, oferecemos
                "Cobrar cartão padrão agora" no lugar de Reenviar/Sincronizar.
                Cobrar tokenizado dispara débito real → reutiliza o diálogo
                reforçado (digitação de ANTECIPAR + auditoria obrigatória).
                Visível apenas em status que admitem nova tentativa. */}
            {hasDefaultCard &&
              canAnticipatePerm &&
              ['aguardando_pagamento', 'pendente', 'edicao_andamento', 'vencido', 'aguardando_emissao'].includes(resolvedStatus || '') && (
              <Button
                size="xs"
                variant="outline"
                className="gap-1.5 bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                onClick={() => setAnteciparOpen(true)}
                disabled={anteciparCiclo.isPending}
                title={`Debita imediatamente ${(defaultCardSnapshot?.brand || 'cartão').toUpperCase()} •••• ${defaultCardSnapshot?.last4 || ''} — também serve como retry após falha no provider`}
              >
                {anteciparCiclo.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <CreditCard className="h-3.5 w-3.5" />
                )}
                Cobrar cartão padrão agora
              </Button>
            )}

            {/* Reenviar ao provider (quando não sincronizado) — oculto quando
                hasDefaultCard, pois o caminho preferido é o botão acima. */}
            {/* v9.5.0: nunca exibir para conta sem integração (não há provedor) */}
            {!isContaManual && !hasDefaultCard && !hasSynced && isPendente && (
              <Button
                variant="outline"
                size="xs"
                className="gap-1.5"
                onClick={handleResendToProvider}
                disabled={resending}
              >
                {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                Reenviar ao Provider
              </Button>
            )}

            {/* "Sincronizar agora" — força reconciliação imediata com o provider.
                Oculta quando hasDefaultCard (o débito direto é mais previsível). */}
            {!isContaManual && !hasDefaultCard && hasSynced && ['aguardando_pagamento','pendente','edicao_andamento','vencido','parcial'].includes(resolvedStatus || '') && (
              <SyncNowButton cobrancaId={cobranca?.id} />
            )}

            {/* Atalho "Ver recorrência" — abre o modal de detalhes da assinatura vinculada */}
            {((r as any)?.assinatura_id) && (
              <Button
                variant="outline"
                size="xs"
                className="gap-1.5"
                onClick={() => setSubscriptionOpen(true)}
              >
                <Repeat className="h-3.5 w-3.5" />
                Ver recorrência
              </Button>
            )}





            {/* Editar — unificado para pendente/aguardando_pagamento/vencido.
                v2.0.0: a RPC update_financeiro_cobranca é a fonte única de verdade
                e enfileira automaticamente em q_charge quando a cobrança já está
                sincronizada com o provedor. Não precisamos mais de modal separado.
                v9.2.0: oculta o botão quando a cobrança é parcelada e já tem
                parcela finalizada (pago/cancelado/estornado) — nesse caso o
                usuário deve editar parcela a parcela pela tabela de Parcelas. */}
            {/* v9.5.0: conta sem integração permanece editável nos status em aberto */}
            {effectiveCanEdit && canEditParent && (isPendente || (isEditableProviderStatus && (hasSynced || isContaManual))) && (
              <Button
                variant="outline"
                size="xs"
                className="gap-1.5"
                onClick={() => setEditOpen(true)}
              >
                <Pencil className="h-3.5 w-3.5" />
                Editar
              </Button>
            )}


            {/* Atalho "Antecipar" — promove o ciclo aguardando emissão e dispara o provider */}
            {canAntecipar && (
              <Button
                size="xs"
                className="gap-1.5 bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20"
                variant="outline"
                onClick={() => setAnteciparOpen(true)}
                disabled={anteciparCiclo.isPending}
              >
                {anteciparCiclo.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Rocket className="h-3.5 w-3.5" />
                )}
                Antecipar
              </Button>
            )}

            {/* v8.0.0: Cancelar fatura — gated por canDelete */}
            {effectiveCanDelete && canCancelStatus && (
              <Button
                variant="outline"
                size="xs"
                className="gap-1.5 text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
                onClick={() => setCancelOpen(true)}
              >
                <XCircle className="h-3.5 w-3.5" />
                Cancelar fatura
              </Button>
            )}

            {/* v8.0.0: Marcar como paga — gated por canEdit */}
            {effectiveCanEdit && canMarkPaidStatus && firstOpenParcela && (
              <Button
                size="xs"
                className="gap-1.5 bg-green-600 hover:bg-green-700 text-white"
                onClick={() => {
                  // v1.2.0: Resolver IDs do provider para sincronização (receive_in_cash):
                  // - Parcelada: cada parcela tem seu próprio paymentId em metadata.provider_payment_id
                  // - Avulsa: usa o provider_charge_id da cobrança-pai
                  const isParcelada = (r as any)?.tipo_cobranca === 'parcelada';
                  const parcelaProviderId = isParcelada
                    ? ((firstOpenParcela as any)?.metadata?.provider_payment_id as string | undefined) ?? null
                    : (r?.provider_charge_id ?? null);
                  const contaCorrenteIdResolved =
                    (r as any)?.conta_corrente_id || cobranca?.conta_corrente_id || null;

                  setMarkPaidParcela({
                    id: firstOpenParcela.id,
                    numero_parcela: firstOpenParcela.numero_parcela,
                    valor: firstOpenParcela.valor,
                    data_vencimento: firstOpenParcela.data_vencimento,
                    provider_charge_id: parcelaProviderId,
                    conta_corrente_id: contaCorrenteIdResolved,
                  });
                  setMarkPaidOpen(true);
                }}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Marcar como paga
              </Button>
            )}

            {/* v9.4.0: Cancelar recebimento em dinheiro (estorno de baixa manual) —
                aparece somente quando existe parcela paga com paid_source = 'manual'. */}
            {effectiveCanEdit && firstManualPaidParcela && (
              <Button
                size="xs"
                variant="outline"
                className="gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => {
                  setUndoPaidParcela({
                    id: firstManualPaidParcela.id,
                    numero_parcela: firstManualPaidParcela.numero_parcela,
                  });
                  setUndoPaidOpen(true);
                }}
              >
                <Undo2 className="h-3.5 w-3.5" />
                Cancelar recebimento
              </Button>
            )}


          </div>

        </DialogContent>
      </Dialog>

      {/* v1.1.2: CheckoutLinkPopover descontinuado neste modal — slug agora é gerado
          proativamente pelo trigger `trg_ensure_checkout_session`. */}

      {/* Modal de edição — reutiliza CreateChargeModal em modo edição.
          v2.0.0: serve tanto cobranças locais (pendente) quanto sincronizadas
          (aguardando_pagamento/vencido) — a RPC enfileira em q_charge quando necessário. */}
      <CreateChargeModal
        open={editOpen}
        onOpenChange={(v) => {
          setEditOpen(v);
          if (!v) {
            queryClient.invalidateQueries({ queryKey: ['financeiro-cobrancas'] });
            queryClient.invalidateQueries({ queryKey: ['cobranca-details'] });
          }
        }}
        editCobrancaId={cobranca?.id}
      />



      {/* Modal de detalhes do negócio vinculado */}
      {rpcCobranca?.deal_id && (
        <Suspense fallback={null}>
          <DealDetailsModalPremium
            dealId={rpcCobranca.deal_id}
            open={dealModalOpen}
            onOpenChange={setDealModalOpen}
          />
        </Suspense>
      )}

      {/* Modal de detalhes da tarefa vinculada (origem da cobrança) */}
      {(rpcCobranca as any)?.task_id && (
        <Suspense fallback={null}>
          <TaskDetailsModal
            taskId={(rpcCobranca as any).task_id}
            open={taskModalOpen}
            onOpenChange={setTaskModalOpen}
          />
        </Suspense>
      )}



      {/* v8.0.0: Modal de baixa manual */}
      <MarkAsPaidModal
        open={markPaidOpen}
        onOpenChange={setMarkPaidOpen}
        cobrancaId={cobranca?.id || ''}
        parcela={markPaidParcela}
      />

      {/* v8.0.0: Dialog de cancelamento */}
      <CancelChargeDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        cobrancaId={cobranca?.id || ''}
        providerChargeId={r?.provider_charge_id}
        contaCorrenteId={r?.conta_corrente_id}
        providerSlug={r?.provider_slug}
        isProrated={Boolean((r as any)?.is_prorated)}
      />

      {/* v9.4.0: Dialog de estorno de baixa manual (cancelar recebimento em dinheiro) */}
      <UndoManualPaymentDialog
        open={undoPaidOpen}
        onOpenChange={(v) => {
          setUndoPaidOpen(v);
          if (!v) setUndoPaidParcela(null);
        }}
        parcelaId={undoPaidParcela?.id || null}
        cobrancaId={cobranca?.id || ''}
        numeroParcela={undoPaidParcela?.numero_parcela ?? null}
        isSynced={!!r?.provider_charge_id}
        providerSlug={r?.provider_slug}
      />



      {/* v9.1.0: Editar parcela individual — reusa EditChargeProviderModal com prop `parcela` */}
      <EditChargeProviderModal
        cobranca={editParcela ? (r as any) : null}
        parcela={editParcela ? {
          id: editParcela.id,
          cobranca_id: editParcela.cobranca_id || cobranca?.id,
          tenant_id: (r as any)?.tenant_id || '',
          numero_parcela: editParcela.numero_parcela,
          valor: editParcela.valor,
          data_vencimento: editParcela.data_vencimento,
          status: editParcela.status,
          metadata: editParcela.metadata,
        } as any : null}
        open={editParcelaOpen}
        onOpenChange={(v) => {
          setEditParcelaOpen(v);
          if (!v) {
            setEditParcela(null);
            queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobranca?.id] });
          }
        }}
      />

      {/* v9.6.0: Edição de parcela em conta SEM integração (controle manual) */}
      <EditParcelaManualModal
        parcela={editParcelaManual ? {
          id: editParcelaManual.id,
          numero_parcela: editParcelaManual.numero_parcela,
          valor: editParcelaManual.valor,
          data_vencimento: editParcelaManual.data_vencimento,
          metadata: editParcelaManual.metadata,
        } : null}
        open={editParcelaManualOpen}
        onOpenChange={(v) => {
          setEditParcelaManualOpen(v);
          if (!v) {
            setEditParcelaManual(null);
            queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobranca?.id] });
          }
        }}
      />

      {/* v9.6.0: Cancelamento de parcela individual (apenas conta sem integração) */}
      <AlertDialog
        open={!!cancelParcelaManual}
        onOpenChange={(v) => { if (!v) setCancelParcelaManual(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar parcela #{cancelParcelaManual?.numero_parcela}?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta parcela deixa de ser cobrada e o valor total da cobrança passa a ser
              a soma das parcelas ativas. A ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSavingParcelaManual}>Voltar</AlertDialogCancel>
            <AlertDialogAction
              disabled={isSavingParcelaManual}
              onClick={async (e) => {
                e.preventDefault();
                const alvo = cancelParcelaManual;
                if (!alvo) return;
                await updateParcelaManual({
                  parcela_id: alvo.id,
                  acao: 'cancel',
                  cancel_reason: 'Cancelamento manual pelo tenant',
                });
                setCancelParcelaManual(null);
                queryClient.invalidateQueries({ queryKey: ['cobranca-details', cobranca?.id] });
              }}
            >
              Cancelar parcela
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* v9.3.0: Cancelamento individual de parcela removido — usar "Cancelar fatura" no topo (cancela em cascata todas as parcelas em aberto) */}


      {/* v9.0.0: Lightbox interno do comprovante de baixa manual */}
      <ReceiptLightboxHost
        receipt={receiptLightbox}
        onClose={() => setReceiptLightbox(null)}
      />

      {/* Feature 2 — Diálogo reforçado de antecipação (digite "ANTECIPAR" para confirmar) */}
      <AnticipateConfirmDialog
        open={anteciparOpen}
        onOpenChange={setAnteciparOpen}
        isPending={anteciparCiclo.isPending}
        customerName={customerName}
        valor={Number((r as any)?.valor_total || 0)}
        moeda={(r as any)?.moeda || 'BRL'}
        dataVencimentoOriginal={(r as any)?.data_vencimento ?? null}
        cicloNumero={((r as any)?.ciclo_numero as number | null) ?? null}
        // Feature 1: cartão padrão da recorrência (snapshot seguro vindo da RPC v1.5.8)
        cardInfo={defaultCardSnapshot}
        onConfirm={() => {
          if (!cobranca?.id) return;
          const dv = (r as any)?.data_vencimento as string | null | undefined;
          const ymd = (dv || '').substring(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
          let dias = 0;
          if (ymd) {
            const [, y, m, d] = ymd;
            const venc = new Date(Number(y), Number(m) - 1, Number(d));
            const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
            dias = Math.max(0, Math.round((venc.getTime() - hoje.getTime()) / 86400000));
          }
          anteciparCiclo.mutate(
            {
              cobrancaId: cobranca.id,
              auditPayload: {
                valor: Number((r as any)?.valor_total || 0),
                dias_antecipados: dias,
                step_up_method: 'typed_keyword:ANTECIPAR',
              },
            },
            { onSuccess: () => setAnteciparOpen(false) },
          );
        }}
      />

      {/* Modal de detalhes da recorrência — aberto pelo atalho "Ver recorrência" no rodapé */}
      <SubscriptionDetailsModal
        assinaturaId={((r as any)?.assinatura_id as string | null | undefined) ?? null}
        open={subscriptionOpen}
        onOpenChange={setSubscriptionOpen}
      />

      {/* v7.3.0: Modal "Memória de cálculo" — apresenta breakdown completo do valor da cobrança */}
      <CobrancaMemoriaCalculoModal
        open={memoriaOpen}
        onOpenChange={setMemoriaOpen}
        cobrancaId={r?.id ?? null}
      />

      {/* v7.4.0: ficha oficial do cliente aberta por cima da cobrança (sem fechá-la) */}
      <CustomerDetailsModalPremium
        open={customerModalOpen}
        onOpenChange={setCustomerModalOpen}
        customerId={chargeCustomerId}
      />
    </>

  );
}

/**
 * SyncNowButton — botão "Sincronizar agora" exibido no rodapé do modal
 * para cobranças sincronizadas com algum provider. Aplica cooldown de 30s
 * (server-side + UX local) e dispara `request_single_charge_reconciliation`.
 */
function SyncNowButton({ cobrancaId }: { cobrancaId: string | undefined }) {
  const { sync, cooldownLeft, isPending, canSync } = useSyncCharge(cobrancaId);
  return (
    <Button
      variant="outline"
      size="xs"
      className="gap-1.5"
      onClick={sync}
      disabled={!canSync}
      title="Consulta o status atual no provider de pagamento"
    >
      {isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <RefreshCw className="h-3.5 w-3.5" />
      )}
      {cooldownLeft > 0 ? `Aguarde ${cooldownLeft}s` : 'Sincronizar agora'}
    </Button>
  );
}

